/* Whether the workspace on screen is read-only — STORY-045.
 *
 * ── why this exists ──────────────────────────────────────────────────────────
 * The lock is enforced on the server and works: every write in an over-limit
 * workspace is refused. What it did not do was LOOK locked. Every field stayed
 * editable and every Save button stayed live, so the page invited an edit and
 * only said no afterwards — reported on 2026-09-05 as "I can still change
 * everything under an event", against a workspace refusing every write.
 *
 * A control that cannot work must say so before it is pressed, not after.
 *
 * ── it disables submitting, not typing ───────────────────────────────────────
 * Deliberate. A half-typed note is worth keeping while somebody goes and frees
 * a seat, and clearing the form would punish them for reading the banner
 * second. The Save is the thing that cannot succeed, so the Save is the thing
 * that is disabled.
 *
 * ── the escapes are unaffected, and by construction ──────────────────────────
 * Deleting, moving an Event to Completed or Cancelled, and everything on the
 * billing screen are click handlers rather than form submissions, so none of
 * them is touched by this. That is the same allow-list the middleware keeps,
 * arrived at from the other side — and it is worth stating because a future
 * "tidy-up" that turned a delete into a submit would silently trap somebody
 * inside a workspace they cannot reduce.
 */

import { createContext, type ReactNode, useContext } from 'react';

const WorkspaceLockContext = createContext(false);

export interface WorkspaceLockProviderProps {
  /** True when the workspace exceeds its plan on any meter. */
  readonly locked: boolean;
  readonly children: ReactNode;
}

export function WorkspaceLockProvider({ locked, children }: WorkspaceLockProviderProps) {
  return <WorkspaceLockContext.Provider value={locked}>{children}</WorkspaceLockContext.Provider>;
}

/**
 * Is this workspace read-only?
 *
 * Defaults to **false** with no provider above, which is what the signed-out
 * screens want: signing in, signing up and accepting an invitation are not
 * inside a workspace and have no plan to be over.
 */
export const useWorkspaceLocked = (): boolean => useContext(WorkspaceLockContext);
